import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Space, Typography, Alert, Divider, Table, Tag, message } from 'antd';
import {
  lookupMainMeterBySubMeter,
  rebootRelatedMains,
  getMqttMappingByMeter,
  mqttPublishByMeter,
  getMqttLatestMessage,
} from '../api';
import {
  YJ_REMOTE_MQTT_WAIT_MS_TIER2,
  adminRemoteRebootCurrent,
  adminCollectReboot52Statuses,
  normalizePublishKey12,
  getAdminMeterPublishKey,
  rxTopicsForHttpPoll,
  pickMustAckPublishKeysFromRebootData,
} from '../utils/yjMeterRemote';

const { Text, Paragraph } = Typography;

const isMainMeter = (device) => Number(device?.device_detail_type) === 1;

const meterNoOf = (device) =>
  String(device?.current_meter_no || device?.factory_meter_no || '').trim();

const ackStatusTag = (status) => {
  switch (status) {
    case 'ok':
      return <Tag color="success">已回包·成功</Tag>;
    case 'fail':
      return <Tag color="error">已回包·失败</Tag>;
    case 'timeout':
      return <Tag color="warning">超时未回</Tag>;
    case 'skip':
      return <Tag>未下发·不监听</Tag>;
    case 'pending':
      return <Tag color="processing">等待回包…</Tag>;
    default:
      return <Tag>-</Tag>;
  }
};

/**
 * 重启弹窗：
 * - 「重启电表」：下发与回包分离；逐表展示谁有回调；主表用本表回包判成败；副表只展示各表回包
 * - 「仅重启当前电表」：本机 publish-by-meter + 等本表 ACK（旧逻辑）
 */
const MeterRebootModal = ({ open, device, onClose }) => {
  const [rebootLoading, setRebootLoading] = useState(false);
  const [selfLoading, setSelfLoading] = useState(false);
  const [rebootHint, setRebootHint] = useState('');
  const [rebootError, setRebootError] = useState('');
  const [rowList, setRowList] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [mainMeterNos, setMainMeterNos] = useState([]);
  const cancelledRef = useRef(false);

  const selfIsMain = isMainMeter(device);
  const busy = rebootLoading || selfLoading;

  useEffect(() => {
    if (!open) {
      cancelledRef.current = true;
      return;
    }
    cancelledRef.current = false;
    setRebootLoading(false);
    setSelfLoading(false);
    setRebootHint('');
    setRebootError('');
    setRowList([]);
    setLookupLoading(false);
    setLookupError('');
    if (isMainMeter(device)) {
      const no = meterNoOf(device);
      setMainMeterNos(no ? [no] : []);
    } else {
      setMainMeterNos([]);
    }
  }, [open, device?.id]);

  const patchRowAck = (key, patch) => {
    const k = normalizePublishKey12(key);
    setRowList((prev) =>
      prev.map((r) => (r.publishKey === k ? { ...r, ...patch } : r))
    );
  };

  const handleRebootRelated = async () => {
    if (!device?.id) return;
    setRebootLoading(true);
    setRebootHint('正在准备监听（先听后发）…');
    setRebootError('');
    setRowList([]);

    let outcomeMeta = {
      isMain: selfIsMain,
      selfKey: '',
      watchKeys: [],
      mustKeys: [],
    };

    try {
      const selfPublishKey = getAdminMeterPublishKey(device);
      const selfKey = normalizePublishKey12(selfPublishKey);
      if (!selfKey) {
        throw new Error('无法确定本表发布键，请检查设备档案');
      }

      const mapRes = await getMqttMappingByMeter(selfPublishKey);
      if (!mapRes?.success || !mapRes?.data?.rx_topic) {
        throw new Error(mapRes?.msg || mapRes?.error || '未找到该设备的 MQTT 主题映射');
      }
      const earlyTopics = rxTopicsForHttpPoll(mapRes.data);
      // 下发前打点，并留 500ms 余量，避免接口耗时期间快回包被 sent_at 滤掉
      const publishAt = Date.now() - 500;

      setRebootHint('监听就绪后将下发关联重启…');

      const { byKey } = await adminCollectReboot52Statuses({
        watchKeys: [selfKey],
        rxTopics: earlyTopics,
        getMqttMappingByMeter,
        getMqttLatestMessage,
        waitMs: YJ_REMOTE_MQTT_WAIT_MS_TIER2,
        publishAt,
        useWebSocket: true,
        httpPollMs: 600,
        shouldAbort: () => cancelledRef.current,
        onUpdate: ({ key, status, ackHex }) => {
          if (cancelledRef.current) return;
          patchRowAck(key, { ackStatus: status, ackHex: ackHex || '' });
        },
        afterListenReady: async () => {
          if (cancelledRef.current) return { watchKeys: [], rxTopics: [] };
          setRebootHint('监听已就绪，正在下发关联重启指令…');
          const res = await rebootRelatedMains({ device_id: device.id });
          if (cancelledRef.current) return { watchKeys: [], rxTopics: [] };

          const data = res?.data || {};
          const targets = Array.isArray(data.targets) ? data.targets : [];
          const results = Array.isArray(data.results) ? data.results : [];
          const resultById = {};
          results.forEach((r) => {
            if (r && r.device_id != null) resultById[r.device_id] = r;
          });

          const mainNos = [];
          targets.forEach((t) => {
            if (String(t.role || '') !== 'main') return;
            const no = String(t.current_meter_no || t.publish_key || '').trim();
            if (no && !mainNos.includes(no)) mainNos.push(no);
          });
          if (mainNos.length) setMainMeterNos(mainNos);

          const rows = targets.map((t) => {
            const pubKey = normalizePublishKey12(t.publish_key);
            const pub = resultById[t.device_id];
            const published = !!(pub && pub.ok);
            const role = String(t.role || '') === 'self' ? '自己' : '主表';
            return {
              key: `${t.device_id}-${pubKey}`,
              deviceId: t.device_id,
              publishKey: pubKey,
              meterNo: String(t.current_meter_no || pubKey || '').trim() || '-',
              role,
              roleRaw: String(t.role || ''),
              sourceRxTopic: String(t.source_rx_topic || '').trim(),
              published,
              publishError: pub && !pub.ok ? String(pub.error || '发布失败') : '',
              ackStatus: published ? 'pending' : 'skip',
              ackHex: '',
              mustAck: false,
            };
          });

          if (!res?.success) {
            setRowList(rows);
            throw new Error('重启失败（指令下发失败）');
          }

          const isMain = data.is_main != null ? !!data.is_main : selfIsMain;
          const mustKeys = pickMustAckPublishKeysFromRebootData({
            ...data,
            is_main: isMain,
          });
          const mustSet = new Set(mustKeys);
          const watchKeys = rows
            .filter((r) => r.published && r.publishKey)
            .map((r) => r.publishKey);
          const topics = Array.isArray(data.rx_topics)
            ? data.rx_topics.map((t) => String(t || '').trim()).filter(Boolean)
            : [];
          const rxTopics = [...topics, ...rows.map((r) => r.sourceRxTopic).filter(Boolean)];

          // 表格仍听全部已下发；「判定」列标出必回目标，方便同事看成败口径
          setRowList(
            rows.map((r) => ({
              ...r,
              mustAck: !!(r.publishKey && mustSet.has(r.publishKey)),
            }))
          );

          outcomeMeta = {
            isMain,
            selfKey: normalizePublishKey12(
              rows.find((r) => r.roleRaw === 'self')?.publishKey || selfKey
            ),
            watchKeys,
            mustKeys,
          };

          if (!watchKeys.length) {
            throw new Error('没有可监听的下发成功目标');
          }
          if (!mustKeys.length) {
            throw new Error(
              isMain
                ? '无法确定本表发布键，无法判定重启成功'
                : '未找到最新主题主表，无法判定重启成功'
            );
          }

          setRebootHint(
            isMain
              ? '指令已下发。成败看本表回包；表格仍逐表展示全部回调…'
              : '指令已下发。成败看最新主题主表回包；表格仍逐表展示全部回调…'
          );
          message.success('指令已下发');

          return { watchKeys, rxTopics };
        },
      });

      if (cancelledRef.current) return;

      Object.keys(byKey).forEach((k) => {
        const st = byKey[k];
        patchRowAck(k, { ackStatus: st.status, ackHex: st.ackHex || '' });
      });

      const { isMain, watchKeys, mustKeys } = outcomeMeta;
      const must = (mustKeys || []).filter(Boolean);
      const allStats = (() => {
        const okN = watchKeys.filter((k) => byKey[k]?.status === 'ok').length;
        const failN = watchKeys.filter((k) => byKey[k]?.status === 'fail').length;
        const toN = watchKeys.filter((k) => byKey[k]?.status === 'timeout').length;
        return `全部回包：成功 ${okN} / 失败 ${failN} / 未回 ${toN}（共 ${watchKeys.length}）`;
      })();

      if (!must.length) {
        setRebootHint(`下发完成，但缺少判定目标。${allStats}`);
        setRebootError('无法按主/副表规则判定重启成功');
        message.warning('缺少判定目标');
      } else {
        const mustOk = must.every((k) => byKey[k]?.status === 'ok');
        const mustFail = must.some((k) => byKey[k]?.status === 'fail');
        const mustPending = must.some(
          (k) => !byKey[k] || byKey[k].status === 'pending' || byKey[k].status === 'timeout'
        );
        if (mustOk) {
          setRebootHint(
            (isMain
              ? '重启成功（本表已回包确认）'
              : '重启成功（最新主题主表已全部确认）') + `。${allStats}`
          );
          setRebootError('');
          message.success('重启成功');
        } else if (mustFail) {
          setRebootHint(`判定目标回包失败。${allStats}`);
          setRebootError(
            isMain
              ? '本表重启回包失败'
              : '最新主题主表存在回包失败（请看表格「判定」列）'
          );
          message.error('重启失败');
        } else if (mustPending) {
          setRebootHint(`判定目标未全部确认。${allStats}`);
          setRebootError(
            isMain
              ? '本表超时未回包'
              : '最新主题主表未全部确认（请看表格「判定」列）'
          );
          message.warning('未确认');
        }
      }
    } catch (e) {
      if (cancelledRef.current) return;
      const errMsg = e?.response?.data?.msg || e?.message || '重启失败';
      setRebootHint('');
      setRebootError(errMsg.includes('重启') || errMsg.includes('下发') || errMsg.includes('监听') || errMsg.includes('判定') ? errMsg : `重启失败：${errMsg}`);
      message.error('重启失败');
    } finally {
      if (!cancelledRef.current) setRebootLoading(false);
    }
  };

  const handleRebootCurrent = async () => {
    if (!device) return;
    setSelfLoading(true);
    setRebootHint('正在对本表下发重启指令并等待回包…');
    setRebootError('');
    setRowList([]);
    try {
      await adminRemoteRebootCurrent({
        device,
        getMqttMappingByMeter,
        mqttPublishByMeter,
        getMqttLatestMessage,
        useWebSocket: true,
      });
      if (cancelledRef.current) return;
      setRebootHint('重启成功（当前电表已确认回包）');
      setRebootError('');
      message.success('重启成功');
    } catch (e) {
      if (cancelledRef.current) return;
      const errMsg = e?.response?.data?.msg || e?.message || '重启失败';
      setRebootHint('');
      setRebootError(errMsg.includes('重启') ? errMsg : `重启失败：${errMsg}`);
      message.error('重启失败');
    } finally {
      if (!cancelledRef.current) setSelfLoading(false);
    }
  };

  const handleLookupMain = async () => {
    if (!device) return;
    const q = meterNoOf(device);
    if (!q) {
      message.warning('当前设备无表号，无法查询主表');
      return;
    }
    setLookupLoading(true);
    setLookupError('');
    try {
      const res = await lookupMainMeterBySubMeter(q);
      if (!res?.success) {
        setLookupError(res?.msg || '查询失败');
        return;
      }
      const list = Array.isArray(res.data?.main_meters) ? res.data.main_meters : [];
      const nos = [];
      list.forEach((m) => {
        const no = String(m?.current_meter_no || m?.factory_meter_no || '').trim();
        if (no && !nos.includes(no)) nos.push(no);
      });
      setMainMeterNos(nos);
      if (!res.data?.latest_rx_topic) {
        setLookupError('未找到该表最近心跳，无法反查主表');
      } else if (!nos.length) {
        setLookupError('已找到 RX 主题，但未匹配到主表');
      } else {
        message.success(`找到 ${nos.length} 个主表表号`);
      }
    } catch (e) {
      setLookupError(e?.response?.data?.msg || e?.message || '查询失败');
    } finally {
      setLookupLoading(false);
    }
  };

  const meterLabel = meterNoOf(device) || device?.device_mac || device?.id || '';

  const columns = [
    {
      title: '表号',
      dataIndex: 'meterNo',
      key: 'meterNo',
      ellipsis: true,
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 72,
    },
    {
      title: '判定',
      key: 'mustAck',
      width: 72,
      render: (_, r) =>
        r.mustAck ? <Tag color="blue">计入</Tag> : <Tag>仅看</Tag>,
    },
    {
      title: '下发',
      key: 'pub',
      width: 88,
      render: (_, r) =>
        r.published ? <Tag color="success">已下发</Tag> : <Tag color="error">失败</Tag>,
    },
    {
      title: '回包',
      key: 'ack',
      width: 120,
      render: (_, r) => ackStatusTag(r.ackStatus),
    },
    {
      title: '说明',
      key: 'note',
      ellipsis: true,
      render: (_, r) => {
        if (r.publishError) return r.publishError;
        if (r.ackStatus === 'fail') return `ACK=${r.ackHex || '-'}`;
        if (r.sourceRxTopic) return r.sourceRxTopic;
        return '-';
      },
    },
  ];

  return (
    <Modal
      title={`重启${meterLabel ? ` · ${meterLabel}` : ''}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnClose
      maskClosable={false}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text strong>重启操作</Text>
          <Paragraph type="secondary" style={{ marginBottom: 8, marginTop: 4 }}>
            <div>
              <b>重启电表</b>：先听后发；下发到本表 + 最近最多 3 主题同房主表。
              表格逐表展示全部回包；<b>成败判定</b>：主表看本表，副表看最新主题主表（「判定=计入」）。
              {selfIsMain
                ? ' 当前档案为主表。'
                : ' 当前档案为副表。'}
            </div>
            <div style={{ marginTop: 6 }}>
              <b>仅重启当前电表</b>：只对本表发 52，先听后发，必须等本表 ACK=0。
            </div>
          </Paragraph>
          <Button
            type="primary"
            danger
            loading={rebootLoading}
            disabled={busy && !rebootLoading}
            onClick={handleRebootRelated}
          >
            重启电表
          </Button>
          <div style={{ minHeight: 40, marginTop: 8 }}>
            {rebootError ? (
              <Alert type="error" showIcon message={rebootError} />
            ) : rebootHint ? (
              <Alert type={busy ? 'info' : 'success'} showIcon message={rebootHint} />
            ) : null}
          </div>
          {rowList.length > 0 ? (
            <Table
              size="small"
              style={{ marginTop: 12 }}
              pagination={false}
              rowKey="key"
              columns={columns}
              dataSource={rowList}
            />
          ) : null}
        </div>

        <div style={{ marginTop: -8 }}>
          <Button
            size="small"
            loading={selfLoading}
            disabled={busy && !selfLoading}
            onClick={handleRebootCurrent}
          >
            仅重启当前电表
          </Button>
          <Divider style={{ margin: '4px 0 0' }} />
        </div>

        <div>
          <Text strong>主表表号（查询）</Text>
          <Paragraph type="secondary" style={{ marginBottom: 8, marginTop: 4 }}>
            {selfIsMain
              ? '当前设备档案为 4G 主表，显示本表表号；也可用查询核对。'
              : '与系统设置「主表查询」同款反查，仅展示，不代替上方重启目标。'}
          </Paragraph>

          {selfIsMain ? (
            <Alert
              type="success"
              showIcon
              message={
                mainMeterNos.length
                  ? `主表表号：${mainMeterNos.join('、')}`
                  : '主表档案缺少表号'
              }
            />
          ) : null}

          <Space direction="vertical" size="small" style={{ width: '100%', marginTop: selfIsMain ? 8 : 0 }}>
            <Button loading={lookupLoading} disabled={busy} onClick={handleLookupMain}>
              查询主表
            </Button>
            {!selfIsMain && mainMeterNos.length > 0 ? (
              <Alert type="success" showIcon message={`主表表号：${mainMeterNos.join('、')}`} />
            ) : null}
            {lookupError ? <Alert type="error" showIcon message={lookupError} /> : null}
          </Space>
        </div>
      </Space>
    </Modal>
  );
};

export default MeterRebootModal;
